import React, { useCallback, useState, useRef } from 'react';
import { Upload, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../ui';

interface FileUploadAreaProps {
  file: File | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  acceptedTypes?: string[];
  maxSizeText?: string;
  title?: string;
  description?: string;
  className?: string;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  file,
  onFileSelect,
  onFileRemove,
  acceptedTypes = ['.xlsx', '.xls'],
  maxSizeText = 'up to 50MB',
  title,
  description,
  className = '',
}) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use translations as default values if props are not provided
  const displayTitle = title || t('tools.fileUpload.title');
  const displayDescription = description || t('tools.fileUpload.description');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const validFile = files.find((file) => {
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        return acceptedTypes.includes(extension);
      });

      if (validFile) {
        onFileSelect(validFile);
      }
    },
    [acceptedTypes, onFileSelect]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
          {displayTitle}
        </CardTitle>
        <CardDescription className="text-sm sm:text-base">
          {displayDescription}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className={`border-2 border-dashed rounded-lg p-4 sm:p-6 lg:p-8 text-center transition-colors ${
            isDragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-center space-x-2">
                <FileSpreadsheet className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                <div className="text-left">
                  <p className="font-medium text-green-700 text-sm sm:text-base break-all">
                    {file.name}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t('tools.fileUpload.fileSize', {
                      size: (file.size / 1024 / 1024).toFixed(2),
                    })}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={onFileRemove} size="sm">
                {t('tools.fileUpload.uploadDifferentFile')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <Upload className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto" />
              <div>
                <p className="text-base sm:text-lg font-medium text-gray-900">
                  {t('tools.fileUpload.dropFileHere')}
                </p>
                <p className="text-sm sm:text-base text-gray-500">
                  {t('tools.fileUpload.clickToBrowse')}
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-2">
                  {t('tools.fileUpload.supportedFormats', {
                    formats: acceptedTypes.join(', '),
                    maxSize: maxSizeText,
                  })}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedTypes.join(',')}
                onChange={handleFileInputChange}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                className="text-sm sm:text-base"
              >
                {t('tools.fileUpload.browseFiles')}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
